import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiUpload } from "react-icons/fi";
import Spinner from "../../components/spinner";
import Pagination from "../../components/elements/pagination";
import Errors from "../errors";
import ConfirmationModal from "../../components/elements/ConfirmationModal";
import useUploadfile from "../../hooks/product/useUploadFile";
import useGetProductUploadList from "../../hooks/product/useGetProductUploadList";
import useDownload from "../../hooks/product/useDownload";
import ProductExcelUploadModal from "../../components/elements/modals/ProductExcelUploadModal";
import useCreate from "../../hooks/useCreate";

const ProductUpload = () => {
  const [currentUploadId, setCurrentUploadId] = useState(""); // To store current upload ID

  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null); // To store selected brand ID for re-upload

  const uploadRef = useRef({});
  const uploadnormalRef = useRef(null);

  const { setDataToServer } = useUploadfile({
    url: "direct_product_excel_upload",
    // pass necessary options
    refreshUrl: "product_excel_upload_listing",
    onSuccess: (data) => {},
    onError: (error) => {
      console.error("Error uploading file:", error);
    },
  });

  const [fileName, setFileName] = useState("");
  const showFileName = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setFileToUpload(file); // Store the file to be uploaded
      setUploadModalOpen(true); // Open the modal
    } else {
      setFileName("");
      setFileToUpload(null);
    }
  };

  const clearFileName = () => {
    setFileName("");
    setFileToUpload(null);
    if (uploadRef.current) {
      uploadRef.current.value = "";
    }
  };

  const {
    data,
    handlePageClick,
    handleSearchVal,
    paramsObject,
    handleSetLimit,
  } = useGetProductUploadList();

  const [category, setCategory] = useState(data.data?.data || []);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleUploadConfirm = () => {
    if (fileToUpload) {
      // Dynamically include upload_id only if currentUploadId is not null
      const requestData = {
        excel_file: fileToUpload,
        brand_id: selectedBrandId, // Use selectedBrandId for re-upload
        ...(currentUploadId && { upload_id: currentUploadId }), // Include upload_id only if it's not null
      };

      setDataToServer.mutate(requestData);
    }
    setModalOpen(false); // Close modal after confirmation
  };

  const handleDelete = (id) => {
    setCategory(id);
    setDeleteModalOpen(true);
  };

  const { downloadFile: downloadFileHook } = useDownload({
    url: "product_excel_upload_download", // Your download URL
    onSuccess: () => toast.success("File downloaded successfully"),
    onError: (err) => toast.error("No product exist for this category"),
  });

  const { setDataToServer: downloadProductExcelReq } = useCreate({
    url: "product_excel_upload_download",
    responseAsBlob: true,
    onSuccess: (data, reqParams) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product_sheet_${reqParams.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // clean up

      toast.success("File downloaded successfully", {
        duration: 2500,
      });
    },
    onError: (error) => {
      toast.error("Error occurred while downloading file");
    },
  });

  const handleDownload = (id) => {
    downloadProductExcelReq.mutate({
      id: id,
    });
  };

  const handleFileChange = (e, uploadId) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFileToUpload(file);

      setCurrentUploadId(uploadId); // Set the upload ID for this specific ro
      setModalOpen(true); // Open confirmation modal
      e.target.value = "";
    } else {
      setFileName("");
      setFileToUpload(null);
    }
  };

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [rowNo, setRowNo] = useState(null);

  const toggleUploadModal = (rowNo) => {
    setRowNo(rowNo);
    setShowUploadModal(!showUploadModal);
  };

  return (
    <>
      {showUploadModal && (
        <ProductExcelUploadModal
          isOpen={showUploadModal}
          rowNo={rowNo}
          handleClose={toggleUploadModal}
        />
      )}
      <div className="category px-4">
        <div className="category__head headingBorder__b">
          <h2 className="titleHeading">Uploaded Products</h2>
        </div>
        <div className="category_table py-5 space-y-5">
          <div className="flex items-end justify-end space-x-4">
            <div class="flex flex-col justify-center items-center underline-offset-1 underline gap-2">
              <button
                className="flex gap-2 items-center "
                type="submit"
                onClick={toggleUploadModal}
              >
                <FiUpload />
                Upload
              </button>
              <input
                ref={uploadnormalRef}
                onChange={(e) => handleFileChange(e, null)}
                type="file"
                accept=".xls, .xlsx"
                id="fileInput"
                className="hidden"
              />
            </div>

            <div>
              <label htmlFor="" className="label">
                Limit
              </label>
              <select
                name="limit"
                value={data.data?.per_page}
                className="input"
                onChange={handleSetLimit}
              >
                {Array(5)
                  .fill(1)
                  .map((_, idx) => {
                    return (
                      <option key={idx} value={(idx + 1) * 10}>
                        {(idx + 1) * 10}
                      </option>
                    );
                  })}
              </select>
            </div>
          </div>
          {data.isLoading || data.isFetching ? (
            <Spinner />
          ) : data.status === "error" ? (
            <Errors errorObject={data.error} inline />
          ) : (
            <>
              <div className=" bg-white rounded-lg shadow overflow-auto relative">
                <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
                  <thead>
                    <tr className="text-left">
                      <th className="bg-gray-100  sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        <p className="w-24">File Name</p>
                      </th>
                      <th className="bg-gray-100  sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        <p className="w-24">Brand Name</p>
                      </th>
                      <th className="bg-gray-100  sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        <p className="w-44">Uploaded by</p>
                      </th>
                      <th className="bg-gray-100  sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        <p className="w-44">Uploaded at</p>
                      </th>
                      <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        Status
                      </th>
                      <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        Action
                      </th>
                      <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center">
                        Download
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data?.data?.map((productData, idx) => {
                      const {
                        id,
                        brand_name,
                        brand_id,
                        name,
                        file_name,
                        status,
                        uploaded_at,
                      } = productData || {};

                      return (
                        <tr key={idx}>
                          <td className="border-dashed border-t border-gray-200 text-center firstName">
                            <div className=" p-2 border-r text-xs ">
                              {file_name}
                            </div>
                          </td>
                          <td className="border-dashed border-t border-gray-200 text-center firstName">
                            <div className=" p-2 border-r text-xs ">
                              {brand_name}
                            </div>
                          </td>
                          <td className="border-dashed border-t border-gray-200 text-center firstName">
                            <div className=" p-2 border-r text-xs ">{name}</div>
                          </td>
                          <td className="border-dashed border-t border-gray-200 text-center firstName">
                            <div className=" p-2 border-r text-xs ">
                              {uploaded_at}
                            </div>
                          </td>
                          <td className="border-dashed border-t border-gray-200 firstName border-r text-center">
                            <span className="text-gray-700 py-3  text-xs">
                              {status}
                            </span>
                          </td>
                          <td className="border-dashed border-t border-gray-200 firstName border-r text-center">
                            <span
                              onClick={() => {
                                setSelectedBrandId(brand_id);
                                uploadRef.current[idx].click();
                              }} // Trigger the file input click to select file
                              className="text-xs bg-primary text-white px-2 py-2 rounded-lg cursor-pointer"
                            >
                              Re-Upload
                            </span>
                            <input
                              ref={(element) =>
                                (uploadRef.current[idx] = element)
                              } // Reference to the file input
                              onChange={(e) => handleFileChange(e, id)} // Handle file change
                              type="file"
                              accept=".xls, .xlsx"
                              id="fileInput"
                              className="hidden"
                            />
                          </td>
                          <td className="border-dashed border-t text-center justify-center items-center border-gray-200 firstName border-r">
                            <button
                              download={true}
                              target="blank"
                              className="flex-1 text-blue-500 underline text-sm cursor-pointer text-center items-center"
                              onClick={() => handleDownload(id)}
                            >
                              Download product
                            </button>
                          </td>
                          {/* <td className="border-dashed border-t border-gray-200 emailAddress">
                        <div className="flex items-center justify-center">
                          <Link to={`edit-category/${id}?mode=edit`}>
                            <IconButton>
                              <EditIcon />
                            </IconButton>
                          </Link>
                          <RiDeleteBinLine
                            onClick={() => handleDelete(id)}
                            className="text-lg cursor-pointer text-gray-600"
                          />
                        </div>
                      </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <Pagination
                  currentPage={+data.data?.page_no}
                  lengthofItems={data.data?.total_count}
                  limit={data.data?.per_page}
                  pageCount={Math.ceil(
                    data.data?.total_count / data.data?.per_page
                  )}
                  onPageChange={handlePageClick}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* <DeleteModal
    open={isDeleteModalOpen}
    handleClose={() => setDeleteModalOpen(false)}
    title={"Delete Category"}
    message={"Are you sure you want to delete this category?"}
    onDelete={() => handleSubmit({ id: category })}
  /> */}
      <ConfirmationModal
        open={isModalOpen}
        handleClose={() => setModalOpen(false)}
        title="Confirm Upload"
        message={`Are you sure you want to upload the file: ${fileName}?`}
        onConfirm={handleUploadConfirm}
        confirmText="Upload"
        cancelText="Cancel"
        confirmColor="primary"
        cancelColor="secondary"
      />
    </>
  );
};

export default ProductUpload;
